<template>
  <div class="hit-container-map">
    <div class="header">
      <slot name="header" />
    </div>

    <div
      v-if="errorMessage"
      class="error-message"
    >
      {{ errorMessage }}
    </div>

    <div
      class="map-container"
      :class="{'has-error': errorMessage}"
    >
      <div v-if="!localPosition || isLoading" />
      <hit-map
        v-else
        ref="mapContainer"
        :position="localPosition"
        :title="title"
        @update:location="handleLocationUpdate"
      >
        <template #footer>
          <slot name="footer">
            <div class="flex flex-col w-full">
              <div class="flex flex-col">
                <div class="flex justify-end items-center">
                  <div class="flex flex-col gap-2 sm:flex-row mt-2">
                    <hit-button
                      :label="t('hit-base.common.save-location')"
                      color="accent"
                      prefix-icon="save"
                      @click="saveLocation"
                    />
                    <hit-button
                      :label="t('hit-base.common.reset-location')"
                      color="warning"
                      prefix-icon="location"
                      @click="removeLocation"
                    />
                  </div>
                </div>
              </div>
            </div>
          </slot>
        </template>
      </hit-map>

      <div
        v-if="isLoading"
        class="spinner-overlay"
      >
        <hit-spinner size="lg" />
      </div>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {useI18n} from 'vue-i18n';
import {HitMap, HitButton} from '@hit/components';
import {ActionService, DataService} from '@hit/base/src';
import HitSpinner from '@hit/components/src/components/icon/HitSpinner.vue';

export default defineComponent({
  name: 'HitContainerMap',
  emits: ['close'],
  components: {
    HitMap,
    HitButton,
    HitSpinner,
  },
  props: {
    container: {
      type: Object,
      required: true,
    },
    attribute: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      localPosition: null,
      isLoading: true,
      errorMessage: null,
      marker: null,
    };
  },
  methods: {
    hasAddressData() {
      return !!(
        this.container.data?.street ||
        this.container.data?.houseNo ||
        this.container.data?.postcode ||
        this.container.data?.city ||
        this.container.data?.country?.designation
      );
    },
    async geocodeAddress() {
      try {
        this.isLoading = true;
        this.errorMessage = null;

        if (
          this.container.data.street != null ||
          this.container.data.houseNo != null ||
          this.container.data.postcode != null ||
          this.container.data.city != null ||
          this.container.data.country?.designation != null
        ) {
          const response = await ActionService.post('map/geocode', {
            values: {
              street: this.container.data?.street,
              houseNo: this.container.data?.houseNo,
              postcode: this.container.data?.postcode,
              city: this.container.data?.city,
              country: this.container.data?.country?.designation ?? null,
            },
          });
          if (response?.data?.coordinates) {
            this.localPosition = {
              type: 'Point',
              coordinates: [
                response.data.coordinates.lng,
                response.data.coordinates.lat,
              ],
            };

            if (this.$refs.mapContainer) {
              this.$refs.mapContainer.centerMap(
                response.data.coordinates.lat,
                response.data.coordinates.lng
              );
            }
            return `POINT(${this.localPosition.coordinates[0]} ${this.localPosition.coordinates[1]})`;
          } else {
            this.errorMessage = this.t('hit-base.common.address-not-found');
            this.localPosition = await this.initPosition();
          }
        } else {
          await this.initPosition();
        }
      } catch (error) {
        this.errorMessage = this.t('hit-base.common.geocoding-error');
      } finally {
        this.isLoading = false;
      }
    },

    async initPosition() {
      const raw = this.container.data?.[this.attribute];

      if (
        raw &&
        typeof raw === 'object' &&
        raw.type === 'Point' &&
        Array.isArray(raw.coordinates)
      ) {
        return raw;
      }

      if (typeof raw === 'string' && raw.startsWith('POINT')) {
        const match = raw.match(/POINT\(([-\d.]+)\s+([-\d.]+)\)/);
        if (match) {
          const [, lng, lat] = match;
          return {
            type: 'Point',
            coordinates: [parseFloat(lng), parseFloat(lat)],
          };
        }
      }

      try {
        const configResponse = await DataService.read('config', {
          attributes: 'value',
          filters: {label: 'eq.company.address.id'},
        });

        if (configResponse?.data?.[0]?.value) {
          const companyId = configResponse.data[0].value;
          const addressResponse = await DataService.read('address', {
            attributes: 'street,house_no,postcode,city,country,location',
            filters: {id: `eq.${companyId}`},
          });

          if (addressResponse?.data?.[0]) {
            const companyAddress = addressResponse.data[0];

            if (companyAddress.location) {
              return companyAddress.location;
            }

            const response = await ActionService.post('map/geocode', {
              values: {
                street: companyAddress.street,
                houseNo: companyAddress.house_no,
                postcode: companyAddress.postcode,
                city: companyAddress.city,
                country: companyAddress.country?.designation ?? null,
              },
            });

            if (response?.data?.coordinates) {
              if (this.$refs.mapContainer) {
                this.$refs.mapContainer.centerMap(
                  response.data.coordinates.lat,
                  response.data.coordinates.lng
                );
              }
              return {
                type: 'Point',
                coordinates: [
                  response.data.coordinates.lng,
                  response.data.coordinates.lat,
                ],
              };
            }
          }
        }
      } catch (error) {
        console.error('Error getting company location:', error);
      }

      // If all fails, return a default position
      return {
        type: 'Point',
        coordinates: [0.0, 0.0],
      };
    },

    handleLocationUpdate(position) {
      this.localPosition = position;
    },
    saveLocation(close = true) {
      const pointString = `POINT(${this.localPosition.coordinates[0]} ${this.localPosition.coordinates[1]})`;

      if (this.marker != pointString) {
        this.container.handleChange('calculatedLocation', false);
      }

      this.container.handleChange(this.attribute, pointString);
      if (close) {
        this.$emit('close');
      }
    },
    async removeLocation() {
      this.$emit('close');
      const pointString = await this.geocodeAddress();
      this.container.handleChange('calculatedLocation', true);
      this.container.handleChange(this.attribute, pointString);
    },
  },
  async beforeMount() {
    if (!this.container.data?.[this.attribute] && this.hasAddressData()) {
      this.container.handleChange('calculatedLocation', true);
      const pointString = await this.geocodeAddress();
      this.container.handleChange(this.attribute, pointString);
    } else {
      this.localPosition = await this.initPosition();
      this.isLoading = false;
    }
    this.marker = `POINT(${this.localPosition.coordinates[0]} ${this.localPosition.coordinates[1]})`;
  },
});
</script>
<style scoped>
.hit-container-map {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: auto;
  min-height: 600px;
}

.map-container {
  position: relative;
  flex: 1;
  height: 600px;
  transition: height 0.3s ease;
}

.map-container.has-error {
  height: 545px;
}

.spinner-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(222, 222, 222, 0.8);
  z-index: 1000;
  height: 100%;
}

.header {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  font-weight: bold;
}

.error-message {
  width: 100%;
  max-width: none;
  padding: 0.5rem 1rem;
  margin: 0;
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 0.375rem;
  color: #dc2626;
  text-align: center;
  font-size: 0.875rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}
</style>
